import { createFilledElement, fillTemplate } from './fill-template.js';
import { updatePagination, addPaginationInputListeners } from './pagination.js';

const datePickerTagName = 'date-picker'; // See date-picker.js

/**
 * Filter template selectors
 */
export const selectors = Object.freeze({
    // not in a template
    dialog: '.filter__form-dialog',
    form: '.filter__form',
    mobileShow: '.filter__mobile-show-dialog-button',
    mobileShowPlex: '.filter__mobile-show-dialog-button--px',
    desktopSortSelect: 'select[name="desktop-sort"]',
    resultsContentContainer: '.filter__results-content-container',
    // in multiple templates
    chipsContainers: '.filter__chips-container',
    resets: '.filter__form-reset',
    // mobileFormHeader
    mobileClose: '.filter__mobile-form-header-close',
    mobileHeaderBottom: '.filter__mobile-form-header-bottom',
    // textInput
    searchField: '.filter__search-field',
    // filterFacet
    details: '.filter__details',
    detailsSummary: '.filter__details > summary',
    detailsContent: '.filter__details-content',
    facetSearches: '.filter__search-field input[form="none"]',
    checkboxList: '.filter__checkbox-list',
    // filterFacetItem
    checkboxes: 'input[type="checkbox"]',
    tooltips: '.filter__tooltip',
    // mobileFormFooter
    mobileSubmit: '.filter__mobile-form-submit',
    mobileSubmitPlex: '.filter__mobile-form-submit--px',
    // resultsViewOptions
    resultsViewToggles: 'input[name="view"]',
    // desktopChipsContainer
    chipTemplate: '.filter__chip-template',
    // resultsFooter
    paginationForm: '.filter__pagination-form',
});

/**
 * Given an array of form controls, find the matching select or input element and populate its value.
 * @param {Element[]} formControls
 * @param {string} name
 * @param {string} value
 * @param {boolean} check
 * @returns {Element?}
 */
export const fillInput = (formControls, name, value, check) => {
    let formControl = null;
    for (formControl of formControls) { // eslint-disable-line no-restricted-syntax
        if (name === formControl.name) {
            if (formControl.tagName === 'SELECT') {
                formControl.value = value;
                break;
            } if (formControl.tagName === 'INPUT') {
                if (['checkbox', 'radio'].includes(formControl.type)) {
                    if (value === formControl.value) {
                        formControl.checked = check;
                        break;
                    }
                }
                if (['text', 'search'].includes(formControl.type)) {
                    formControl.value = value;
                    break;
                }
                if (formControl.type === 'date') {
                    const datePicker = formControl.closest(datePickerTagName);
                    if (datePicker) {
                        datePicker.clearSelection();
                    } else {
                        formControl.value = value;
                    }
                    break;
                }
            }
        }
    }
    return formControl;
};

const slideUp = (element, duration = 300) => {
    const el = element;

    return new Promise((resolve) => {
        el.style.height = `${element.offsetHeight}px`;
        el.style.transitionProperty = 'height, margin, padding';
        el.style.transitionDuration = `${duration}ms`;
        // eslint-disable-next-line no-unused-expressions
        el.offsetHeight; // trigger reflow
        el.style.overflow = 'hidden';
        el.style.height = 0;
        el.style.paddingTop = 0;
        el.style.paddingBottom = 0;
        el.style.marginTop = 0;
        el.style.marginBottom = 0;
        setTimeout(() => {
            el.style.display = 'none';
            el.style.removeProperty('height');
            el.style.removeProperty('padding-top');
            el.style.removeProperty('padding-bottom');
            el.style.removeProperty('margin-top');
            el.style.removeProperty('margin-bottom');
            el.style.removeProperty('overflow');
            el.style.removeProperty('transition-duration');
            el.style.removeProperty('transition-property');
            resolve();
        }, duration);
    });
};

const slideDown = (element, duration = 300) => {
    const el = element;

    return new Promise((resolve) => {
        el.style.removeProperty('display');
        let { display } = window.getComputedStyle(element);
        if (display === 'none') display = 'block';
        el.style.display = display;
        const height = element.offsetHeight;
        el.style.overflow = 'hidden';
        el.style.height = 0;
        el.style.paddingTop = 0;
        el.style.paddingBottom = 0;
        el.style.marginTop = 0;
        el.style.marginBottom = 0;
        // eslint-disable-next-line no-unused-expressions
        el.offsetHeight; // trigger reflow
        el.style.transitionProperty = 'height, margin, padding';
        el.style.transitionDuration = `${duration}ms`;
        el.style.height = `${height}px`;
        el.style.removeProperty('padding-top');
        el.style.removeProperty('padding-bottom');
        el.style.removeProperty('margin-top');
        el.style.removeProperty('margin-bottom');
        setTimeout(() => {
            el.style.removeProperty('height');
            el.style.removeProperty('overflow');
            el.style.removeProperty('transition-duration');
            el.style.removeProperty('transition-property');
            resolve();
        }, duration);
    });
};

/**
 * Adds slide up/down animation when details element is opened/closed
 * @param {MouseEvent} ev
 */
const handleSummaryClick = (ev) => {
    ev.preventDefault();
    const detailsEl = ev.currentTarget.closest('details');
    const contentEl = detailsEl.querySelector('div');

    if (!detailsEl.open) {
        // Open
        contentEl.style.display = 'none';
        detailsEl.open = true;
        slideDown(contentEl);
    } else {
        // Close
        slideUp(contentEl).then(() => {
            detailsEl.open = false;
        });
    }
};

/**
 * Adds filter facet related listeners
 * @param {HTMLElement} parentElement
 */
export const addFacetListeners = (parentElement) => {

    // Adds listeners necessary to animate filter facet expand/collapse
    parentElement.querySelectorAll(selectors.detailsSummary).forEach(
        (summary) => summary.addEventListener('click', handleSummaryClick)
    );

    // Reorder function for this list
    const reorderSelectedToTop = (list) => {
        const items = Array.from(list.children);
        items.sort((a, b) => {
            const aChecked = a.querySelector('input[type="checkbox"]')?.checked;
            const bChecked = b.querySelector('input[type="checkbox"]')?.checked;

            // Selected first
            if (aChecked && !bChecked) return -1;
            if (!aChecked && bChecked) return 1;

            // If both are the same (selected or unselected), preserve original order
            return Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex);
        });

        // Move elements in new order
        items.forEach((item) => list.appendChild(item));
    };

    const resetOrder = (list) => {
        const items = Array.from(list.children);
        items.sort((a, b) => Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex));
        items.forEach((item) => list.appendChild(item));
    };

    // Select all filter checkbox lists on the page
    const allCheckboxLists = parentElement.querySelectorAll(selectors.checkboxList);

    allCheckboxLists.forEach((checkboxList) => {
        // Add event listener on checkbox change for this list
        checkboxList.addEventListener('change', (event) => {
            if (event.target.type === 'checkbox') {
                reorderSelectedToTop(checkboxList);
            }
        });
    });

    const resetButtons = parentElement.querySelectorAll(selectors.resets);
    resetButtons.forEach((button) => {
        button.addEventListener('click', () => {
            allCheckboxLists.forEach((list) => resetOrder(list));
        });
    });
};

/** @param {HTMLDialogElement} filterDialog */
export const showMobileFilter = (filterDialog) => {
    const dialog = filterDialog;
    document.body.classList.add('no-scroll');
    dialog.style.left = '100%';
    dialog.showModal();
    dialog.style.left = null;
};

/** @param {HTMLDialogElement} filterDialog */
export const closeMobileFilter = (filterDialog) => {
    const dialog = filterDialog;

    document.body.classList.remove('no-scroll');
    window.setTimeout(() => {
        dialog.close();
        dialog.style.left = null;
    }, 500); // Timeout matches the .filter__form-dialog transition duration
    // Cannot use transitionend because the Crownpeak autofix interferes with it
    dialog.style.left = '100%';
};

/**
 * Trigger a callback function when a form's select or non-textual input changes.
 * @param {HTMLFormElement} formElement
 * @param {Function} callback
 */
export const onFormControlChange = (formElement, callback) => {
    [...formElement.elements].forEach((formControl) => {
        if (['SELECT', 'INPUT'].includes(formControl.tagName) && !['text', 'search'].includes(formControl.type)) {
            formControl.addEventListener('change', () => callback());
        }
    });
};

/** @param {Event} ev */
export const adjustTooltipPosition = (ev) => {
    const tooltip = ev.currentTarget.querySelector('.filter__tooltip-content');

    // Ensure that tooltip does not overflow the right viewport
    if (tooltip.getBoundingClientRect().left < 0) {
        tooltip.style.transform = '';
    }
    const rightEdgeDistance = document.documentElement.clientWidth - tooltip.getBoundingClientRect().right - 24;
    if (rightEdgeDistance < 0) {
        tooltip.style.transform = `translateX(${rightEdgeDistance}px)`;
    }
};

/** @param {MouseEvent} ev */
const handleTooltipClick = (ev) => {
    ev.preventDefault(); // Prevent click-through on tooltips
    ev.stopPropagation();
};

/** @param {HTMLElement} tooltip */
export const handleTooltip = (tooltip) => {
    tooltip.addEventListener('click', handleTooltipClick);
    tooltip.addEventListener('focus', adjustTooltipPosition);
    tooltip.addEventListener('mouseenter', adjustTooltipPosition);
};

const searchFilterAttribute = 'data-filtered';

/**
 * @param {HTMLInputElement} checkbox - must match the structure of filter-templates.filterFacetItem
 * @param {number} count
 * @param {boolean} alwaysShow - if true, the item is not hidden even if the count is zero
 */
export const updateFacetItemCount = (checkbox, count, alwaysShow = false) => {
    if (!checkbox) {
        return;
    }

    const listItem = checkbox.closest('li');
    const span = listItem.querySelector('span');
    span.dataset.count = count;

    // check if this item was filtered by the facet search
    const isFiltered = listItem.getAttribute(searchFilterAttribute) === 'true';
    listItem.setAttribute('aria-hidden', (count < 1 && !checkbox.checked && !alwaysShow) || isFiltered);
};

/** @param {InputEvent} ev */
export const handleFacetSearch = (ev) => {
    const input = ev.currentTarget;
    const inputValue = input.value?.toLowerCase();

    const listItems = input.closest('.filter__details-content').querySelectorAll('li');
    listItems.forEach((listItem) => {
        const isMatch = listItem.dataset.label.toLowerCase().includes(inputValue);
        const noResults = listItem.querySelector('span').dataset.count < 1;
        listItem.setAttribute(searchFilterAttribute, !isMatch);
        listItem.setAttribute('aria-hidden', !isMatch || noResults);
    });
};

/**
 * Clears all facet search inputs within a parent element and resets their filtering effects
 * by dispatching a synthetic input event (which triggers the registered handleFacetSearch handler).
 * @param {HTMLElement} parentElement
 */
export const clearFacetSearches = (parentElement) => {
    parentElement.querySelectorAll(selectors.facetSearches).forEach((input) => {
        if (input.value === '') return;
        input.value = '';
        input.dispatchEvent(new Event('input'));
    });
};

/** @param {NodeListOf<Element> | Element[]} filterDetails */
export const showFilterSearchFields = (filterDetails) => {
    filterDetails.forEach((facet) => {
        // Assign original index to li items
        const listItems = facet.querySelectorAll('li');
        listItems.forEach((li, index) => {
            const listItem = li;

            if (!listItem.hasAttribute('data-original-index')) {
                listItem.dataset.originalIndex = index;
            }
        });

        const searchField = facet.querySelector('.filter__search-field');
        if (searchField) {
            const numOptions = [...facet.querySelectorAll('li:not([aria-hidden="true"])')].length;
            const hasDatePicker = !!facet.querySelector(datePickerTagName);
            if (numOptions > 12 || hasDatePicker) {
                searchField.hidden = false;
            } else {
                searchField.hidden = true;
            }
        }
    });
};

/** @param {NodeListOf<Element> | Element[]} filterDetails */
export const hideFacetsWithNoResults = (filterDetails) => {
    filterDetails.forEach((filterFacet) => filterFacet.setAttribute(
        'aria-hidden',
        // Facets with results and date pickers are shown
        filterFacet.querySelectorAll('input[type="date"], li:not([aria-hidden="true"])').length < 1
    ));
};

/**
 * @param {HTMLFormElement} paginationForm
 */
export const addPaginationListeners = (paginationForm) => {
    addPaginationInputListeners(paginationForm);
};

/**
 * @param {HTMLElement} parentElement
 * @param {HTMLFormElement} paginationForm
 * @param {Record<string, number>} data
 */
export const updateResultCounts = (
    parentElement,
    paginationForm,
    {
        firstResultNum,
        lastResultNum,
        totalResults,
        currentPageNum,
        pageCount
    }
) => {
    fillTemplate(parentElement, 'data-x', { firstResultNum, lastResultNum, totalResults }, { keepIfEmpty: true });
    updatePagination(paginationForm, { currentPageNum, pageCount: pageCount || 1 });
};

/**
 * @param {MouseEvent} ev
 * @param {HTMLFormElement} form
 * @param {Function} callback
 * @param {HTMLElement?} parentElement - if provided, the facet search for the removed checkbox is cleared
 */
export const handleChipClick = (ev, form, callback, parentElement = null) => {
    ev.preventDefault();
    const { name, value } = ev.currentTarget.dataset;
    fillInput([...form.elements], name, value, false);
    if (parentElement) {
        // Clear only the facet search input belonging to this checkbox's facet
        const checkbox = parentElement.querySelector(`input[type="checkbox"][name="${name}"][value="${value}"]`);
        const facetDetails = checkbox?.closest(selectors.details);
        const facetSearch = facetDetails?.querySelector(selectors.facetSearches);
        if (facetSearch && facetSearch.value !== '') {
            facetSearch.value = '';
            facetSearch.dispatchEvent(new Event('input'));
        }
    }
    callback();
};

/**
 * @param {HTMLFormElement} filterForm
 * @param {HTMLTemplateElement} chipTemplate
 * @param {Function} chipCallback
 * @param {NodeListOf<Element> | Element[]} chipContainers
 * @param {string[]} otherChipParams
 * @param {HTMLElement?} parentElement - if provided, passed to handleChipClick to clear facet searches on chip removal
 * @returns {boolean} True if the number of chips is nonzero.
 */
export const updateChips = (filterForm, chipTemplate, chipCallback, chipContainers, otherChipParams = [], parentElement = null) => {
    const handledDatePickers = new Set();
    const chipData = [...filterForm.elements]
        .map((formInput) => {
            // Get data from checked checkboxes
            if (formInput.type === 'checkbox' && formInput.checked) {
                return {
                    label: formInput.closest('li').dataset.label,
                    name: formInput.name,
                    value: formInput.value,
                };
            }
            // Get data from date pickers
            if (formInput.type === 'date' && formInput.value !== '') {
                const datePicker = formInput.closest(datePickerTagName);
                if (datePicker && !handledDatePickers.has(datePicker)) {
                    handledDatePickers.add(datePicker);
                    return {
                        label: datePicker.getLabel(),
                        name: datePicker.getName(),
                        value: ''
                    };
                }
            }
            return null;
        })
        .filter((data) => data != null);

    // Get data for other specified params
    const formData = new FormData(filterForm);
    const otherChipData = otherChipParams
        .map((param) => ({ label: formData.get(param)?.toString(), name: param, value: '' }))
        .filter(({ label }) => label != null && label !== '');
    chipData.unshift(...otherChipData);

    const hasChips = chipData.length > 0;

    // Create chips and fill them into chip containers
    chipContainers.forEach((chipContainer) => {
        const container = chipContainer;
        const chips = chipData.map((data) => {
            const chip = createFilledElement(chipTemplate, 'data-xt', data, { keepIfEmpty: true });
            chip.querySelector('button').addEventListener('click', (ev) => handleChipClick(ev, filterForm, chipCallback, parentElement));
            return chip;
        });
        container.replaceChildren(...chips);
        container.hidden = !hasChips;
    });

    return hasChips;
};

/**
 * @param {string} dateStringA
 * @param {string} dateStringB
 * @returns {number}
 */
export const compareDates = (dateStringA, dateStringB) => {
    let dateA;
    let dateB;
    try {
        dateA = new Date(dateStringA.replace(' ', 'T'));
    } catch {
        dateA = null;
    }
    try {
        dateB = new Date(dateStringB.replace(' ', 'T'));
    } catch {
        dateB = null;
    }
    return dateA - dateB;
};

/** @param {HTMLElement} parentElement */
export const addViewToggleListeners = (parentElement) => {
    const resultsContentContainer = parentElement.querySelector(selectors.resultsContentContainer);

    /** @param {Event} ev */
    const handleViewChange = (ev) => {
        resultsContentContainer.dataset.view = ev.currentTarget.value;
    };

    parentElement.querySelectorAll(selectors.resultsViewToggles).forEach(
        (el) => el.addEventListener('change', handleViewChange)
    );
};
